<template>
	<div class="project">
		<div class="project-header px-1 py-1" @click="toggleTaskInput(false)">
			<div class="project-info flex ion-align-items-center" @click="openOptions">
				<img v-if="project?.project_image" class="mr-2" :src="project.project_image">
				<img v-else class="mr-2" src="@/plugins/app/_assets/wzo.jpg">

				<ion-skeleton-text v-if="!project" animated />
				<template v-else>

					<div class="flex flex-col">
						<h4 class="company">{{ project?.contactName }}</h4>
						<h4>{{ project?.name }}</h4>
						<!-- <h6 class="m-0 p-0 ">tracking in <b>{{ project?.organization }}</b> </h6> -->
					</div>
					<template v-if="hasOptions">
						<ion-icon class="ml-auto cursor-pointer size-xl" :icon="$icons.ellipsisVertical"></ion-icon>
						<ion-popover :event="optionsClickEvent" :is-open="isOptions" dismiss-on-select
							@didDismiss="isOptions = false">
							<ion-item button detail="false" @click="toggleUnassignedTasks()">
								<ion-label>Toggle unassigned tasks</ion-label>
								<ion-checkbox slot="end" :checked="areUnassignedsToggled"></ion-checkbox>
							</ion-item>
							<ion-item button detail="false" :routerLink="`/projects/${project._id}`">
								<ion-icon class="size-xl" slot="start" :icon="$icons.apps" size="large"
									color="primary" />
								<ion-label class="mt-0 mb-0">Show details</ion-label>
							</ion-item>
							<ion-item button detail="false" :routerLink="`/analytics?search=${project.name}`">
								<ion-icon class="size-xl" slot="start" :icon="$icons.analytics" size="large"
									color="primary" />
								<ion-label class="mt-0 mb-0">Search for analytics</ion-label>
							</ion-item>
							<ion-item v-if="project.notion_project?._id" :disabled="notionTasksRequest" button
								detail="false" @click="importNotionTasks()">
								<ion-icon class="size-xl" slot="start" :icon="$icons.cloud" size="large"
									color="primary" />
								<ion-label class="mt-0 mb-0">Sync notion tasks</ion-label>
							</ion-item>
						</ion-popover>
					</template>
				</template>
			</div>

			<template v-if="tabs?.length">
				<div class="flex">
					<ion-skeleton-text v-if="!project" animated class="mt-1" />
					<ion-segment v-if="project && !revealSearch" v-model="activeTab" scrollable
						class="ion-align-items-center swiper-no-swipe" @ionChange="onTabChange(activeTab, project)">
						<ion-segment-button v-for="tab in tabs" :key="tab" :value="tab">{{ tab[0].toUpperCase() +
							tab.slice(1) }}</ion-segment-button>
					</ion-segment>
					<ion-searchbar v-if="revealSearch" :value="inProjectSearchQuery"
						@ionInput="inProjectSearchQuery = $event.target.value" @ionClear="inProjectSearchQuery = ''"
						ref="inProjectSearch" class="mt-1 mb-0 h-[36px] slide-in-anim" />
					<svg class="mt-1.5 !w-3 !h-3 ml-0.5 text-primary" xmlns="http://www.w3.org/2000/svg" width="28"
						height="28" viewBox="0 0 512 512" aria-label="Search tasks in this project"
						@click="toggleProjectSearchBar">
						<title>Search</title>
						<path
							d="M456.69 421.39L362.6 327.3a173.81 173.81 0 0034.84-104.58C397.44 126.38 319.06 48 222.72 48S48 126.38 48 222.72s78.38 174.72 174.72 174.72A173.81 173.81 0 00327.3 362.6l94.09 94.09a25 25 0 0035.3-35.3zM97.92 222.72a124.8 124.8 0 11124.8 124.8 124.95 124.95 0 01-124.8-124.8z"
							fill="currentColor"></path>
					</svg>
				</div>
			</template>
		</div>

		<div class="project-content" ref="tasks-wrapper" @scroll="onTasksSlide" @click="toggleTaskInput(false)">
			<slot>
				<template v-if="project">
					<p v-if="!tasks?.length && tabs?.length" class="ml-4 mr-4 text-center">{{ `You don't have any
						${activeTab} tasks in this project!`}}</p>
					<div v-else>
						<Task-card v-for="(task, i) in searchTasks" v-show="showTask(i)" v-bind="taskProps" :key="task"
							:task="task" :project="project" class="first:mt-1" :ref="`task-${task._id}`" />
						<p v-if="!searchTasks?.length" class="ml-4 mr-4 text-center">{{ `You don't have any tasks with
							"${inProjectSearchQuery}" in this project!`}}</p>
					</div>

				</template>
				<ion-skeleton-text v-else v-for="num in 6" :key="num" animated class="mb-1" />
			</slot>
		</div>

		<div class="flex project-footer" :class="{ hidden: !canAddTask }">
			<template v-if="canAddTask">
				<ion-skeleton-text v-if="!project" class="mt-1 ml-1 mr-1" animated />
				<div v-else-if="!showInput"
					class="project-add-task text flex ion-justify-content-center ion-align-items-center w-full h-full"
					@click="toggleTaskInput(true)">
					<h3 class="mr-2">+</h3>
					<p>Create a new task</p>
				</div>

				<div v-else @ionBlur="toggleTaskInput()"
					class="project-add-task flex ion-align-items-center ion-justify-content-center w-full h-full px-1">
					<ion-input v-model="newTaskName" :maxlength="250" placeholder="Create new task" autofocus
						@keyup.enter="addTask()" ref="taskInput" />
					<ion-icon class="size-xxl ml-1" :icon="$icons.arrowForwardCircle" @click.stop="addTask()" />
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import TaskCard from '@/plugins/lib@task/task-card.vue'

import axios from '@/plugins/w/axios/models/axios'
const api = {
	addNewTask: (name, projectId) => axios.post_auth_data('/tasks', { name, projectId }),
	getUnassignedTasks: (projectId) => axios.get_auth_data(`/projects/${projectId}/unassignedTasks`),
	importNotionTasks: (notionProjectId) => axios.post_auth_data(`/notion_projects/${notionProjectId}/syncForUser`),
	getProjectTasks: (projectId) => axios.get_auth_data(`/projects/${projectId}/tasks`)
}

export default {
	components: { TaskCard },

	props: {
		projectProp: {
			required: true
		},

		hasOptions: {
			type: Boolean,
			default: true
		},

		canAddTask: {
			type: Boolean,
			default: true
		},

		removeTaskOnUpdate: {
			type: Boolean,
			default: false
		},

		taskProps: {
			type: Object
		},

		tabs: {
			type: Array,
			default: () => (['upcoming', 'completed'])
		},

		onTabChange: {
			type: Function,
			default: () => { }
		},

		tasksFilter: {
			type: Function,
			default: (task, activeTab) => !!task.completed == (activeTab == 'completed')
		}
	},

	data() {
		return {
			project: null,
			showInput: false,
			newTaskName: '',
			activeTab: this.tabs?.[0],

			activeIndex: 0,
			visibleTasksCount: 5,

			optionsClickEvent: null,
			notionTasksRequest: null,
			isOptions: false,
			areUnassignedsToggled: false,

			inProjectSearchQuery: '',
			revealSearch: false
		}
	},

	computed: {
		tasks() {
			return this.project.tasks?.filter(task => this.tasksFilter?.(task, this.activeTab) ?? true)
		},
		searchTasks() {
			if (this.inProjectSearchQuery.length !== 0) {
				return this.project.tasks?.filter(task => 
					task.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(this.inProjectSearchQuery.toLowerCase())
				)
			} else {
				return this.project.tasks?.filter(task => this.tasksFilter?.(task, this.activeTab) ?? true)
			}
		}
	},

	mounted() {
		if (!(this.project = this.projectProp))
			return

		this.$eventBus.on('updateTask', this.onUpdateTask)
		this.$eventBus.on('removeTask', this.onRemoveTask)

		window.addEventListener('resize', this.getVisibleTasksCount)

		this.tasksWrapper = this.$refs['tasks-wrapper']
		setTimeout(this.getVisibleTasksCount, 10)
	},

	unmounted() {
		this.$eventBus.off('updateTask', this.onUpdateTask)
		this.$eventBus.off('removeTask', this.onRemoveTask)

		window.removeEventListener('resize', this.getVisibleTasksCount)
	},

	methods: {
		_scrollToTopOfList() {
			// if (this.tasksWrapper)
			// 	this.tasksWrapper.scrollTop = 0
		}, 

		_removeTask(task) {
			const taskIndex = this.project.tasks?.findIndex(obj => obj._id == task._id)
			if (taskIndex > -1)
				this.project.tasks.splice(taskIndex, 1)
		},

		_addTask(task) {
			this.project.tasks?.unshift(task)

			this._scrollToTopOfList()
		},

		openOptions(ev) {
			this.optionsClickEvent = ev
			this.isOptions = this.hasOptions
		},

		async toggleUnassignedTasks() {
			if (this.areUnassignedsToggled) {
				this.areUnassignedsToggled = false
				return this.project.tasks = this.project.tasks?.filter(task => !task.unassigned)
			}

			const unassignedTasks = (await api.getUnassignedTasks(this.project._id)).map(task => {
				task.unassigned = true
				return task
			})
			this.project.tasks?.unshift(...unassignedTasks)
			this.areUnassignedsToggled = true
		},

		onUpdateTask(data) {
			const isThisProject = this.project?._id == (data?.projectId ?? data?.task.projectId)
			if (!isThisProject)
				return

			const task = data.task ? data.task : data
			this._removeTask(task)

			if (!this.removeTaskOnUpdate)
				this._addTask(task)
		},

		onRemoveTask(data) {
			const isThisProject = this.project?._id == (data?.projectId ?? data?.task.projectId)
			if (isThisProject)
				this._removeTask(data.task ? data.task : data)
		},

		getVisibleTasksCount() {
			return this.visibleTasksCount = Math.ceil(this.tasksWrapper.clientHeight / this.getTaskHeight()) || 20
		},

		showTask(index) {
			return this.activeIndex + this.visibleTasksCount + 1 >= index
		},

		onTasksSlide() {
			this.activeIndex = Math.floor(this.tasksWrapper.scrollTop / this.getTaskHeight())
		},

		getTaskHeight() {
			return 46
		},

		toggleTaskInput(show) {
			if (show) {
				setTimeout(() => this.$refs.taskInput.$el.setFocus(), 10)
				return this.showInput = true
			}

			this.showInput = false
			this.newTaskName = ''
		},

		async addTask() {
			this.showInput = false

			const taskName = this.newTaskName.trim()
			if (!taskName) return

			try {
				const newTask = await api.addNewTask(taskName, this.project._id)
				this.$eventBus.emit('updateTask', newTask)
				this.newTaskName = ''

				this._scrollToTopOfList()
				this.$refs.taskInput?.$el.setBlur()
			} catch (err) {
				console.error(err)
			}
		},

		async importNotionTasks() {
			try {
				if (this.notionTasksRequest) return

				this.notionTasksRequest = true
				this.$toast.success('Syncing notion tasks!')

				await api.importNotionTasks(this.project.notion_project._id)

				const updatedTasks = await api.getProjectTasks(this.project._id)


				this.project.tasks = updatedTasks

				this.$toast.success('Notion tasks successfully synced for ' + this.project.name + '!')
			} catch (e) {
				this.$toast.error('Unable to sync notion tasks for ' + this.project.name + '!\nError: ' + (e.message || e))
			} finally {
				this.notionTasksRequest = false
			}
		},



		toggleProjectSearchBar() {
			this.revealSearch = !this.revealSearch
			if (!this.revealSearch) {
				this.inProjectSearchQuery = ''
			} else {
				setTimeout(() => { this.$refs.inProjectSearch.$el.setFocus() }, 50)
			}

		}
	}
}
</script>

<style lang="sass" scoped>
[theme="dark"]
	.project
		border: 1px solid var(--ion-color-medium-tint)

	.project-footer
		ion-icon
			color: white !important

		.project-add-task *
			color: white !important

ion-popover
	--width: 300px !important

.project
	box-shadow: 0px 0px 10px var(--ion-color-light-tint)
	border-radius: 8px
	width: calc(100% - 10px)
	height: calc(100% - 40px)
	margin: 20px 5px 40px
	display: flex
	flex-direction: column
	overflow: hidden

	.project-header
		border-bottom: 1px solid var(--ion-color-medium-tint)
		flex: 0 0 auto

		h4
			margin: 0
			font-size: 17px
			line-height: 17px

			&.company
				font-size: 16px
				line-height: 16px
				margin-bottom: 5px
				color: var(--ion-color-medium-tint)

		img
			width: 60px
			height: 60px
			object-fit: cover
			border-radius: 100px
			border: 1px solid var(--ion-color-medium-tint)

	.project-content
		padding: 0 14px
		overflow-y: auto
		overflow-x: none
		scroll-behavior: smooth
		flex: 0 1 100%

		ion-skeleton-text
			height: 60px

	.project-footer
		border-top: 1px solid var(--ion-color-medium-tint)
		position: relative
		min-height: 50px

		ion-icon
			color: var(--ion-color-primary)

		.project-add-task
			position: absolute
			top: 0
			left: 0
			z-index: 10
			border-radius: 8px
			cursor: pointer

			&.text *
				color: var(--ion-color-primary)
				line-height: 13px
				margin: 0

	ion-skeleton-text 
		--border-radius: 5px
		margin: 5px 0
		height: 26px

ion-searchbar
	padding: 0 !important
	transition: .24s
	animation: slide-in-anim 0.5s forwards
	-webkit-animation: slide-in-anim 0.5s forwards
	display: block

</style>